import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ConversationId, Seq } from '@cyphex/types';
import { chatApi } from '../api';
import { READ_FLUSH_MS } from '../constants';
import { clearUnread } from '../lib/readStateCache';

/**
 * Coalesces read receipts: scrolling through 200 messages sends ONE request,
 * not 200. Only fires while the tab is visible, and never sends a seq it has
 * already acknowledged.
 */
export function useMarkRead(
  conversationId: ConversationId | null,
  visibleSeq: Seq,
  atBottom: boolean,
  /**
   * The server's own `lastReadSeq`. When it moves BACKWARDS the user marked the
   * chat unread, and the local "already acknowledged up to N" memo has to be
   * forgotten — otherwise re-opening the chat would leave the badge stuck on,
   * because this hook would think it had already sent that receipt.
   */
  serverLastReadSeq: Seq = 0,
): void {
  const client = useQueryClient();
  const acknowledged = useRef<Map<string, Seq>>(new Map());

  useEffect(() => {
    if (!conversationId) return;
    const local = acknowledged.current.get(conversationId);
    if (local !== undefined && serverLastReadSeq < local) {
      acknowledged.current.delete(conversationId);
    }
  }, [conversationId, serverLastReadSeq]);

  useEffect(() => {
    if (!conversationId || visibleSeq <= 0 || !atBottom) return undefined;
    if ((acknowledged.current.get(conversationId) ?? 0) >= visibleSeq) return undefined;
    if (document.visibilityState !== 'visible') return undefined;

    const handle = setTimeout(() => {
      acknowledged.current.set(conversationId, visibleSeq);
      clearUnread(client, conversationId, visibleSeq);
      void chatApi.markRead(conversationId, { seq: visibleSeq }).catch(() => {
        acknowledged.current.delete(conversationId);
      });
    }, READ_FLUSH_MS);

    return () => clearTimeout(handle);
  }, [atBottom, client, conversationId, serverLastReadSeq, visibleSeq]);
}
